import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';

import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';
import { SuccessModalComponent } from 'src/app/shared/modals/success-modal/success-modal.component';
import { UtilitiesService } from 'src/app/services/utilities/utilities.service';
import { Router } from '@angular/router';
import {
  handleFileUploaded,
  handleFileUploadError,
} from 'src/app/shared/components/upload-input/file-upload-helper';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PaymentModalComponent } from 'src/app/shared/modals/payment-modal/payment-modal.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-marriage-registration-application',
  standalone: false,
  templateUrl: './marriage-registration-application.component.html',
  styleUrl: './marriage-registration-application.component.scss',
})
export class MarriageRegistrationApplicationComponent {
  isSubmitting: boolean;
  isVerifyingAsin: boolean;
  isLoading: boolean;
  countries: any[];
  states: any[];
  lgas: any[];
  previewed: boolean;
  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private dialog: MatDialog,
    private utilitiesService: UtilitiesService,
    private router: Router,
    private authService: AuthService,
  ) {}

  showErrorMessages: boolean = false;

  form: FormGroup;

  streetTypes: any[];

  today: string;

  ngOnInit(): void {
    this.initializeForm();

    this.getCountries();

    this.getStates();

    this.today = new Date().toISOString().split('T')[0];
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      // === File Uploads ===
      oathCertificate: [null, Validators.required],

      bachelorBirthCertificate: [null, Validators.required],
      bachelorDeclarationOfAge: [null, Validators.required],
      bachelorPassportPhotograph: [null, Validators.required],
      bachelorIndigeneCertificate: [null, Validators.required],
      bachelorInternationalPassport: [null],

      // === Bachelor ===
      bachelorFirstName: ['', Validators.required],
      bachelorMiddleName: [''],
      bachelorSurname: ['', Validators.required],
      bachelorPhoneNumber: [
        '',
        [Validators.required, this.phoneNumberValidator() as ValidatorFn],
      ],
      bachelorDob: ['', Validators.required],
      bachelorCountry: [null, Validators.required],
      bachelorOccupation: ['', Validators.required],
      bachelorLengthOfResidence: ['', Validators.required],
      bachelorState: [null],
      bachelorLga: [null],
      bachelorAddress: ['', Validators.required],
      bachelorSignature: [null, Validators.required],
      bachelorHasAnotherMarriage: [false],
      bachelorIsWidower: [false],
      bachelorIsDivorce: [false],
      bachelorHasChild: [false],

      bachelorWitnessMiddleName: [''],
      bachelorWitnessSurname: ['', Validators.required],
      bachelorWitnessFirstName: ['', Validators.required],
      bachelorWitnessEmail: ['', this.emailValidator()],
      bachelorWitnessPhoneNumber: [
        '',
        [Validators.required, this.phoneNumberValidator() as ValidatorFn],
      ],
      bachelorWitnessAddress: ['', Validators.required],
      bachelorWitnessSignature: [null, Validators.required],

      // === Spinster ===
      spinsterBirthCertificate: [null, Validators.required],
      spinsterDeclarationOfAge: [null, Validators.required],
      spinsterPassportPhotograph: [null, Validators.required],
      spinsterIndigeneCertificate: [null, Validators.required],
      spinsterInternationalPassport: [null],

      spinsterLengthOfResidence: ['', Validators.required],
      spinsterOccupation: ['', Validators.required],
      consent: ['', Validators.required],
      spinsterFirstName: ['', Validators.required],
      spinsterMiddleName: [''],
      spinsterSurname: ['', Validators.required],
      spinsterPhoneNumber: [
        '',
        [Validators.required, this.phoneNumberValidator() as ValidatorFn],
      ],
      spinsterDob: ['', Validators.required],
      spinsterCountry: [null, Validators.required],
      spinsterState: [null],
      spinsterLga: [null],
      spinsterAddress: ['', Validators.required],
      spinsterSignature: [null, Validators.required],
      spinsterHasAnotherMarriage: [false],
      spinsterIsWidow: [false],
      spinsterIsDivorce: [false],
      spinsterHasChild: [false],

      spinsterWitnessFirstName: ['', Validators.required],
      spinsterWitnessSurname: ['', Validators.required],
      spinsterWitnessMiddleName: [''],
      spinsterWitnessEmail: ['', this.emailValidator()],
      spinsterWitnessPhoneNumber: [
        '',
        [Validators.required, this.phoneNumberValidator() as ValidatorFn],
      ],
      spinsterWitnessAddress: ['', Validators.required],
      spinsterWitnessSignature: [null, Validators.required],
    });
  }

  get oathCertificateUploadControl(): FormControl {
    return this.form?.get('oathCertificate')! as FormControl;
  }

  getCountries() {
    this.isLoading = true;
    this.apiService
      .get('/utility/all/countries')
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response: any) => {
        this.countries = response.map((country) => ({
          ...country,
          formattedName: this.toTitleCase(country.name.replace(/_/g, ' ')),
        }));
      });
  }

  loadLga(state: any) {
    this.isLoading = true;
    this.apiService
      .get('/utility/all/lgas/' + state?.id)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response: any) => {
        this.lgas = response.map((lga) => ({
          ...lga,
          formattedName: this.toTitleCase(lga.name.replace(/_/g, ' ')),
        }));
      });
  }

  getStates() {
    this.isLoading = true;
    this.apiService
      .get('/utility/all/state')
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response: any) => {
        this.states = response.map((state) => ({
          ...state,
          formattedName: this.toTitleCase(state.name.replace(/_/g, ' ')),
        }));
      });
  }

  toTitleCase(str: string): string {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }

  previewForm() {
    this.previewed = true;
    this.form.disable();
  }

  enableEdit() {
    this.previewed = false;
    this.form.enable();
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }

      const cleanedPhoneNumber = control.value
        .replace(/\s/g, '')
        .replace(/-/g, '');

      const isDigitsOnly = /^\d+$/.test(cleanedPhoneNumber);

      if (
        cleanedPhoneNumber.length === 0 ||
        (cleanedPhoneNumber.length === 11 && isDigitsOnly) ||
        (cleanedPhoneNumber.length === 13 &&
          cleanedPhoneNumber.startsWith('234')) ||
        (cleanedPhoneNumber.length === 14 &&
          cleanedPhoneNumber.startsWith('+234'))
      ) {
        return null;
      } else {
        return { invalidPhoneNumber: true };
      }
    };
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

      if (control.value && !emailPattern.test(control.value)) {
        return { invalidEmail: true };
      }

      return null;
    };
  }

  onFileChange(
    fileDetails: { file: File; readableUrl: string } | null,
    controlName: string,
  ) {
    handleFileUploaded(fileDetails, this.form, controlName);
  }

  onError(errorResponse: any, controlName: string) {
    handleFileUploadError(errorResponse, this.form, controlName);
  }

  openSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate(['/admin/marriage-registration']);
    });
  }

  openErrorModal(message: string) {
    let dialogRef = this.dialog.open(ErrorModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { errorMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  openConfirmModal() {
    this.dialog.open(ConfirmActionComponent, {
      width: '400px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: [
        'modal-form',
        'operation-modal',
        'modal-no-overflow-changed',
      ],
      disableClose: true,

      data: {
        title: 'Submit Marriage Registration?',
        body: '',
        confirmText: 'Yes',
        lottieIconPath: 'assets/lottie/alert.json',
        onConfirm: () => {
          this.create();
        },
        onCancel: () => {},
        showCancelButton: true,
        showConfirmButton: true,
      },
    });
  }

  async create(): Promise<void> {
    this.isSubmitting = true;

    try {
      const formData = this.getFormValues();

      const request = await this.mapFormValuesToObject(formData);

      const url = '/marriage/register';

      this.apiService
        .post(url, request)
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
          }),
        )
        .subscribe(
          (res) => {
            if (res.success) {
              this.openSuccessModal(
                'Marriage Registration submitted successfully',
              );
            } else {
              this.openErrorModal(res.error || res.message);
            }
          },
          (error) => {
            this.openErrorModal(error?.message);
          },
        );
    } catch (error) {
      this.isSubmitting = false;
      this.openErrorModal('Failed to process form data');
    }
  }

  getFormValues() {
    const formData = {};
    Object.keys(this.form.controls).forEach((control) => {
      formData[control] = this.form.get(control)?.value;
    });
    return formData;
  }

  async mapFormValuesToObject(formValue: any): Promise<any> {
    let bachelorSignatureBase64 = '';
    let spinsterSignatureBase64 = '';
    let bachelorWitnessSignatureBase64 = '';
    let spinsterWitnessSignatureBase64 = '';

    let bachelorBirthCertificateBase64 = '';
    let bachelorDeclarationOfAgeBase64 = '';
    let bachelorPassportPhotographBase64 = '';
    let bachelorIndigeneCertificateBase64 = '';
    let bachelorInternationalPassportBase64 = '';

    let spinsterBirthCertificateBase64 = '';
    let spinsterDeclarationOfAgeBase64 = '';
    let spinsterPassportPhotographBase64 = '';
    let spinsterIndigeneCertificateBase64 = '';
    let spinsterInternationalPassportBase64 = '';

    if (formValue.bachelorSignature?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.bachelorSignature.file,
      );
      bachelorSignatureBase64 = fullBase64.split(',')[1];
    }
    if (formValue.spinsterSignature?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.spinsterSignature.file,
      );
      spinsterSignatureBase64 = fullBase64.split(',')[1];
    }
    if (formValue.bachelorWitnessSignature?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.bachelorWitnessSignature.file,
      );
      bachelorWitnessSignatureBase64 = fullBase64.split(',')[1];
    }
    if (formValue.spinsterWitnessSignature?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.spinsterWitnessSignature.file,
      );
      spinsterWitnessSignatureBase64 = fullBase64.split(',')[1];
    }

    if (formValue.bachelorBirthCertificate?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.bachelorBirthCertificate.file,
      );
      bachelorBirthCertificateBase64 = fullBase64.split(',')[1];
    }
    if (formValue.bachelorDeclarationOfAge?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.bachelorDeclarationOfAge.file,
      );
      bachelorDeclarationOfAgeBase64 = fullBase64.split(',')[1];
    }
    if (formValue.bachelorPassportPhotograph?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.bachelorPassportPhotograph.file,
      );
      bachelorPassportPhotographBase64 = fullBase64.split(',')[1];
    }
    if (formValue.bachelorIndigeneCertificate?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.bachelorIndigeneCertificate.file,
      );
      bachelorIndigeneCertificateBase64 = fullBase64.split(',')[1];
    }
    if (formValue.bachelorInternationalPassport?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.bachelorInternationalPassport.file,
      );
      bachelorInternationalPassportBase64 = fullBase64.split(',')[1];
    }

    if (formValue.spinsterBirthCertificate?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.spinsterBirthCertificate.file,
      );
      spinsterBirthCertificateBase64 = fullBase64.split(',')[1];
    }
    if (formValue.spinsterDeclarationOfAge?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.spinsterDeclarationOfAge.file,
      );
      spinsterDeclarationOfAgeBase64 = fullBase64.split(',')[1];
    }
    if (formValue.spinsterPassportPhotograph?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.spinsterPassportPhotograph.file,
      );
      spinsterPassportPhotographBase64 = fullBase64.split(',')[1];
    }
    if (formValue.spinsterIndigeneCertificate?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.spinsterIndigeneCertificate.file,
      );
      spinsterIndigeneCertificateBase64 = fullBase64.split(',')[1];
    }
    if (formValue.spinsterInternationalPassport?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.spinsterInternationalPassport.file,
      );
      spinsterInternationalPassportBase64 = fullBase64.split(',')[1];
    }

    return {
      oathCertificate: formValue.oathCertificate.base64.split(',')[1],
      bachelor: {
        firstName: formValue.bachelorFirstName,
        lastName: formValue.bachelorSurname,
        bachelorMiddleName: formValue.bachelorMiddleName,
        phoneNumber: formValue.bachelorPhoneNumber,
        dob: formValue.bachelorDob,
        country: formValue.bachelorCountry,
        state: formValue.bachelorState,
        lga: formValue.bachelorLga,
        address: formValue.bachelorAddress,
        signature: bachelorSignatureBase64,
        hasAnotherMarriage: formValue.bachelorHasAnotherMarriage,
        isWidower: formValue.bachelorIsWidower,
        isDivorce: formValue.bachelorIsDivorce,
        hasChild: formValue.bachelorHasChild,

        occupation: formValue.bachelorOccupation,
        lengthOfResidence: formValue.bachelorLengthOfResidence,

        bachelorBirthCertificate: bachelorBirthCertificateBase64,
        bachelorDeclarationOfAge: bachelorDeclarationOfAgeBase64,
        bachelorPassportPhotograph: bachelorPassportPhotographBase64,
        bachelorIndigeneCertificate: bachelorIndigeneCertificateBase64,
        bachelorInternationalPassport: bachelorInternationalPassportBase64,
      },

      spinster: {
        firstName: formValue.spinsterFirstName,
        spinsterMiddleName: formValue.spinsterMiddleName,
        lastName: formValue.spinsterSurname,
        phoneNumber: formValue.spinsterPhoneNumber,
        dob: formValue.spinsterDob,
        country: formValue.spinsterCountry,
        state: formValue.spinsterState,
        lga: formValue.spinsterLga,
        address: formValue.spinsterAddress,
        signature: spinsterSignatureBase64,
        hasAnotherMarriage: formValue.spinsterHasAnotherMarriage,
        isWidow: formValue.spinsterIsWidow,
        isDivorce: formValue.spinsterIsDivorce,
        hasChild: formValue.spinsterHasChild,

        occupation: formValue.spinsterOccupation,
        consent: formValue.consent,
        lengthOfResidence: formValue.spinsterLengthOfResidence,

        spinsterBirthCertificate: spinsterBirthCertificateBase64,
        spinsterDeclarationOfAge: spinsterDeclarationOfAgeBase64,
        spinsterPassportPhotograph: spinsterPassportPhotographBase64,
        spinsterIndigeneCertificate: spinsterIndigeneCertificateBase64,
        spinsterInternationalPassport: spinsterInternationalPassportBase64,
      },

      witnesses: [
        {
          firstName: formValue.bachelorWitnessFirstName,
          middleName: formValue.bachelorWitnessMiddleName,
          lastName: formValue.bachelorWitnessLastName,
          phoneNumber: formValue.bachelorWitnessPhoneNumber,
          email: formValue.bachelorWitnessEmail,
          address: formValue.bachelorWitnessAddress,
          signature: bachelorWitnessSignatureBase64,
          witnessSide: 'BACHELOR',
        },
        {
          firstName: formValue.spinsterWitnessFirstName,
          middleName: formValue.spinsterWitnessMiddleName,
          lastName: formValue.spinsterWitnessSurname,
          phoneNumber: formValue.spinsterWitnessPhoneNumber,
          email: formValue.spinsterWitnessEmail,
          address: formValue.spinsterWitnessAddress,
          signature: spinsterWitnessSignatureBase64,
          witnessSide: 'SPINSTER',
        },
      ],
    };
  }

  startInterswitch(res: any) {
    const url = '/payment/initialize';
    const details = {
      paymentAmount: 5200,
      callbackUrl: environment.envUrl + 'admin/marriage-registration',
      email: '',
      phoneNumer: this.form.get('bachelorPhoneNumber')?.value,
      asin: this.form.get('bachelorPhoneNumber')?.value,
      revenueName: 'COURT WEDDING/ CERTIFICATE',
      entityId: res?.data?.id,
      entityType: 'MARRIAGE_REGISTRATION',
    };
    this.apiService.post(url, details).subscribe({
      next: (response) => {
        this.checkout(response.data);
      },
      error: (error) => {
        console.info(`Unable to reach Interswitch: ${error}`);
      },
    });
  }

  hasAccess(permissions: string[]) {
    return this.authService.hasAnyPermission(permissions);
  }

  checkout(details: any) {
    var paymentRequest = {
      merchant_code: details?.merchantCode,
      pay_item_id: details?.payItemId,
      txn_ref: details?.orderId,
      cust_id: details?.customerId,
      amount: Math.round((details?.amount * 100 + Number.EPSILON) * 100) / 100,
      currency: '566',
      site_redirect_url: window.location.origin,
      onComplete: (response: any) => this.paymentCallback(response),
      mode: details?.mode,
    };

    if (
      details?.mode === 'LIVE' &&
      details?.splitAccounts &&
      Object.keys(details?.splitAccounts).length > 0
    ) {
      paymentRequest['split_accounts'] = JSON.stringify(details?.splitAccounts);
    }

    (window as any).webpayCheckout(paymentRequest);
  }

  paymentCallback(response: any) {
    const url = '/payment/acknowledge?orderId=' + response?.txnref;
    this.apiService.post(url, {}).subscribe(
      (response) => {
        this.router.navigate(['/admin/marriage-registration']);
      },
      (error) => {
        console.info(`Unable to reach Interswitch: ${error}`);
      },
    );
    return;
  }

  handleFileUpload(event: Event, controlName: string, maxFileSizeInMB: number) {
    let maxFileSize = maxFileSizeInMB * 1024 * 1024;
    const file = (event.target as HTMLInputElement).files?.[0];

    const formControl = this.form.get(controlName);
    formControl?.markAsTouched();

    if (file) {
      if (file.size > maxFileSize) {
        formControl?.setErrors({ maxFileSize: { max: maxFileSize } });

        return;
      }

      if (this.isFilePDF(file) || this.isFileImage(file)) {
        this.convertToBase64(file)
          .then((base64) => {
            formControl?.setValue({ file, base64 });
          })
          .catch((error) => {
            console.error('Error converting file to base64:', error);
          });
      } else {
        console.log('Please upload a valid image or PDF file.');
      }
    }
  }

  isFileImage(file: File): boolean {
    return file?.type?.startsWith('image/') ?? false;
  }

  isFilePDF(file: File): boolean {
    return file?.type === 'application/pdf';
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  convertToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  back() {
    window.history.back();
  }
}
